import React, { useMemo } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Gutter,
  Paragraph,
  Checkbox,
  Form,
} from '@axiom/ui';
import type { User, Contact } from '@axiom/validation';

import { ContactApi } from '../../api/contact-api';
import { UserApi } from '../../api/user-api';

import {
  NotificationPreferencesModalSchema,
  type NotificationPreferencesModal as NotificationPreferencesModalType,
} from './notification-preferences-modal-schema';

export type NotificationPreferencesModalProps = {
  user: User;
  onClose: () => void;
};

export const NotificationPreferencesModal = ({
  user,
  onClose,
}: NotificationPreferencesModalProps) => {
  const disabledOptions = useMemo(() => {
    const data = {
      BenchUpdates: false,
      AvailabilityUpdates: false,
    };

    (user.contact?.accounts || []).forEach(account => {
      (account.emailsOptedOut || []).forEach(item => {
        data[item] = !!item;
      });
    });

    return data;
  }, [user.contact?.accounts]);

  const handleOnSubmit = async (
    changedFields: Partial<NotificationPreferencesModalType>
  ) => {
    if (!user.contact?.id) return;

    const payload: Partial<Contact> = {
      availabilityNotificationOptOut: Object.hasOwn(
        changedFields,
        'availabilityNotification'
      )
        ? !changedFields.availabilityNotification
        : undefined,
      benchNotificationOptOut: Object.hasOwn(changedFields, 'benchNotification')
        ? !changedFields.benchNotification
        : undefined,
    };

    await ContactApi.updateContact(user.contact.id, payload);
    await UserApi.refreshUser();

    onClose();
  };

  return (
    <Form
      name="NOTIFICATION_FORM"
      schema={NotificationPreferencesModalSchema}
      initialValues={{
        availabilityNotification: !user.contact?.availabilityNotificationOptOut,
        benchNotification: !user.contact?.benchNotificationOptOut,
        marketingCommunications: true,
      }}
      onSubmit={handleOnSubmit}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="NOTIFICATION_PREFERENCES">
            <ModalHeader
              name="MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Notification Preferences
            </ModalHeader>
            <ModalSection>
              <Gutter bottom="16px">
                <Paragraph>
                  Choose the bench notifications you want to receive and get
                  relevant updates sent directly to your email.
                </Paragraph>
              </Gutter>
              <Gutter vertical="8px">
                <Checkbox
                  name="benchNotification"
                  label="Bench Updates"
                  displayValue="Let me know when there are additions or changes to talent on my bench."
                  disabled={disabledOptions.BenchUpdates}
                />
              </Gutter>
              <Gutter vertical="8px">
                <Checkbox
                  name="availabilityNotification"
                  label="Availability Updates"
                  displayValue="Let me know when availability changes for talent on my bench."
                  disabled={disabledOptions.AvailabilityUpdates}
                />
              </Gutter>
              <Gutter vertical="8px">
                <Checkbox
                  disabled
                  name="marketingCommunications"
                  label="Marketing Communications"
                  displayValue={
                    <a
                      data-test="MARKETING_UNSUBSCRIBE_LINK"
                      href="https://www.axiomlaw.com/unsubscribe"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Manage your marketing email preferences on AxiomLaw.com.
                    </a>
                  }
                />
              </Gutter>
            </ModalSection>
            <ModalFooter>
              <Button
                name="cancel"
                variation="outline"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>

              <Button name="submit" onClick={fireSubmit}>
                Submit
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
